import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLoaderData, useLocation, useNavigate, useRouteLoaderData } from 'react-router';
import { paths } from '../../../../common/constants/pathVariables.js';
import { CommonErrorType, ModelType } from '../../../../common/enums.js';
import { EmptyOrError, getNotFoundError } from '../../../../components/EmptyOrError/index.js';
import { FixedBroadbandCheckout } from '../../../../components/FixedBroadbandCheckout/FixedBroadbandCheckout.js';
import { applyWizardEvent, searchAddress as searchAddressAction } from '../../../../selfservice/actions/index.js';
import {
  clearFixedBroadbandOrderDraft,
  getFixedBroadbandOrderDraft,
  isFixedBroadbandOrderDraftExpired,
} from '../../../../selfservice/common/fixedBroadbandOrderStorage.js';
import { useAuth } from '../../AuthProvider.js';
import { getNewAddOnVisibilities, resolveSelectedCommercialProductCode } from './fixedBroadbandAddOnUtil.js';
import type { FixedBBCheckoutLoaderData, FixedBBRootLoaderData } from '../../../../common/loaders.js';
import type { CommercialProductAddOnVisibilitiesMap } from '../../../../common/utils/addOnVisibilityUtils.js';
import type { AddOnVisibility } from '../../../../generated/api/addOnVisibility.js';
import type { OnlineModel } from '../../../../generated/api/onlineModel';
import type { FixedBroadbandOrderDraft } from '../../../../selfservice/common/fixedBroadbandOrderStorage.js';
import type { State } from '../../../../selfservice/common/store.js';

export const FixedBroadbandCheckoutPath = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { onlineModels, errors } = useRouteLoaderData('fixedBBRoot') as FixedBBRootLoaderData;
  const addressSearchResult = useSelector((s: State) => s?.resources?.addressSearchResult);
  const { state } = location;
  const fixedBBCheckoutLoaderData = useLoaderData() as FixedBBCheckoutLoaderData;
  const { preLoadedAddOnRules } = fixedBBCheckoutLoaderData;
  const [publicAddOnVisibilities, setPublicAddOnVisibilities] = useState<AddOnVisibility[]>();
  const [commercialProductAddOnVisibilitiesMap, setCommercialProductAddOnVisibilitiesMap] =
    useState<CommercialProductAddOnVisibilitiesMap>({});
  const [restoredDraft, setRestoredDraft] = useState<FixedBroadbandOrderDraft | null>(null);
  // Starts true when OIDC persistence is disabled (no draft to check).
  // When enabled, we always check localStorage before rendering the checkout.
  const [draftCheckComplete, setDraftCheckComplete] = useState(false);

  const oidcStatePersistenceEnabled = useAuth().inMigrationPhase;

  const draftRestorationAttempted = useRef(false);

  useEffect(() => {
    if (!oidcStatePersistenceEnabled || draftRestorationAttempted.current) {
      setDraftCheckComplete(true);
      return;
    }
    draftRestorationAttempted.current = true;

    const draft = getFixedBroadbandOrderDraft();
    if (!draft) {
      setDraftCheckComplete(true);
      return;
    }

    if (isFixedBroadbandOrderDraftExpired(draft)) {
      clearFixedBroadbandOrderDraft();
      setDraftCheckComplete(true);
      if (!state?.selectedOfferCode) {
        navigate(paths.ANONYMOUS_FIXED_BROADBAND_ORDER, { replace: true });
      }
      return;
    }

    // Only restore the draft when it matches the current offer (browser refresh)
    // or when there is no router state yet (post-OIDC redirect).
    if (!state?.selectedOfferCode || draft.routerState.selectedOfferCode === state.selectedOfferCode) {
      setRestoredDraft(draft);
      setDraftCheckComplete(true);
      if (!state?.selectedOfferCode) {
        // Post-OIDC: also restore the router state so the checkout renders.
        navigate(location.pathname, { state: draft.routerState, replace: true });
      }
    } else {
      // Different offer — stale draft, don't apply it.
      setDraftCheckComplete(true);
    }
  }, [oidcStatePersistenceEnabled, state?.selectedOfferCode, navigate, location.pathname]);

  const broadbandOnlineModel: OnlineModel | undefined = state?.isMobileProduct
    ? state?.mobileBroadbandOnlineModel
    : state?.fixedBroadbandOnlineModel;

  const commercialProductCode = resolveSelectedCommercialProductCode(state?.selectedOfferCode, broadbandOnlineModel);

  const yritystuplaModel = onlineModels?.find(onlineModel => onlineModel.onlineModelCode === ModelType.Yritystupla);

  const validationErrors = errors?.filter(err => err.type === CommonErrorType.VALIDATION);

  useEffect(() => {
    if (!addressSearchResult) {
      dispatch(searchAddressAction(state?.searchAddress, state?.postalCode));
    }
  }, [addressSearchResult, dispatch, state?.searchAddress, state?.postalCode]);

  useEffect(() => {
    dispatch(applyWizardEvent(2, 'FIXED_BROADBAND_ORDER'));
  }, [dispatch]);

  useEffect(() => {
    if (!commercialProductCode) {
      return;
    }

    if (commercialProductCode in commercialProductAddOnVisibilitiesMap) {
      return;
    }

    (async () => {
      const newVisibilities = await getNewAddOnVisibilities(commercialProductCode, preLoadedAddOnRules);

      const combinedVisibilities = {
        ...commercialProductAddOnVisibilitiesMap,
        ...newVisibilities.commercialProductAddOnVisibilitiesMap,
      };

      setPublicAddOnVisibilities(newVisibilities.addOnVisibilities);
      setCommercialProductAddOnVisibilitiesMap(combinedVisibilities);
    })();
  }, [commercialProductAddOnVisibilitiesMap, commercialProductCode, preLoadedAddOnRules]);

  // Hold rendering until the draft-restoration effect has committed; prevents FixedBroadbandCheckout
  // from mounting with null restoredDraft props when the OIDC redirect flushes before React batches.
  if (oidcStatePersistenceEnabled && !draftCheckComplete) {
    return null;
  }

  if (
    !(state?.selectedOfferCode && addressSearchResult?.postalCode && addressSearchResult?.match && broadbandOnlineModel)
  ) {
    return !state?.previousUrl ? (
      <EmptyOrError {...getNotFoundError()} onButtonClick={() => navigate(paths.ANONYMOUS_FIXED_BROADBAND_ORDER)} />
    ) : null;
  }

  return (
    <FixedBroadbandCheckout
      yritystuplaModel={yritystuplaModel}
      selectedOfferCode={state?.selectedOfferCode}
      addressSearchMatch={{ ...addressSearchResult?.match, postalCode: addressSearchResult?.postalCode }}
      fixedBroadbandOnlineModel={broadbandOnlineModel!}
      validationErrors={validationErrors}
      isMobileProduct={state?.isMobileProduct}
      commercialProductAddOnVisibilities={commercialProductAddOnVisibilitiesMap}
      publicAddOnVisibilities={publicAddOnVisibilities}
      fixedBBCheckoutLoaderData={fixedBBCheckoutLoaderData}
      addressSearchResult={addressSearchResult ?? undefined}
      savedRouterState={state}
      initialStep1State={restoredDraft?.step1Selections}
      initialActiveStep={restoredDraft?.checkoutStep}
      oidcStatePersistenceEnabled={oidcStatePersistenceEnabled}
    />
  );
};
